import { FormLibrary } from '@goldcast/api/backend';

import { State } from './types';

import { CrmObjectType, FieldMapping, FieldMappingRowError, SourceField } from '@/studio/orgIntegration/store/types';
import {
  getFieldMappingRowErrorDefaultState,
  getFieldMappingsDefaultState,
  getPrevFieldMappingsDefaultState,
  getSaveFieldMappingLoadingDefaultState
} from '@/studio/orgIntegration/store/utils';

const mutations = {
  setCurrentForm(state: State, form: FormLibrary | null) {
    state.currentForm = form;
  },
  setFormSourceFieldLoading(state: State, isFormSourceFieldLoading: boolean) {
    state.isFormSourceFieldLoading = isFormSourceFieldLoading;
  },
  setFormSourceFields(state: State, formSourceFields: SourceField[]) {
    state.formSourceFields = formSourceFields;
  },
  initializeFormFieldMappingStore(state: State, crmObjectTypes: CrmObjectType[]) {
    state.formFieldMappings = getFieldMappingsDefaultState(crmObjectTypes);
    state.prevFormFieldMappings = getPrevFieldMappingsDefaultState(crmObjectTypes);
    state.formFieldMappingRowError = getFieldMappingRowErrorDefaultState(crmObjectTypes);
    state.isSaveFormFieldMappingLoading = getSaveFieldMappingLoadingDefaultState(crmObjectTypes);
  },
  setFormFieldMappingLoading(
    state: State,
    { crmObjectType, isFieldMappingLoading }: { crmObjectType: string; isFieldMappingLoading: boolean }
  ) {
    if (crmObjectType in state.formFieldMappings) {
      state.formFieldMappings[crmObjectType].isLoading = isFieldMappingLoading;
    }
  },
  setFormFieldMappings(
    state: State,
    { crmObjectType, formFieldMappings }: { crmObjectType: string; formFieldMappings: FieldMapping[] }
  ) {
    if (crmObjectType in state.formFieldMappings) {
      state.formFieldMappings[crmObjectType].mappings = formFieldMappings;
    }
  },
  setPrevFormFieldMappings(
    state: State,
    { crmObjectType, prevFormFieldMappings }: { crmObjectType: string; prevFormFieldMappings: FieldMapping[] }
  ) {
    state.prevFormFieldMappings[crmObjectType] = prevFormFieldMappings;
  },
  setFormFieldMappingRowError(
    state: State,
    { crmObjectType, errorsArray }: { crmObjectType: string; errorsArray: (FieldMappingRowError | null)[] }
  ) {
    if (crmObjectType in state.formFieldMappingRowError) {
      state.formFieldMappingRowError[crmObjectType] = errorsArray;
    }
  },
  addNewEmptyFormFieldMappingItem(
    state: State,
    { crmObjectType, emptyFieldMapping }: { crmObjectType: string; emptyFieldMapping: FieldMapping }
  ) {
    if (crmObjectType in state.formFieldMappings) {
      state.formFieldMappings[crmObjectType].mappings = [
        ...state.formFieldMappings[crmObjectType].mappings,
        emptyFieldMapping
      ];
    }
  },
  removeFormFieldMappingItem(
    state: State,
    { crmObjectType, fieldMappingID }: { crmObjectType: string; fieldMappingID: string }
  ) {
    if (crmObjectType in state.formFieldMappings) {
      state.formFieldMappings[crmObjectType].mappings = state.formFieldMappings[crmObjectType].mappings.filter(
        fieldMapping => fieldMapping.id !== fieldMappingID
      );
    }
  },
  updateFormFieldMappingItem(
    state: State,
    { crmObjectType, updatedFieldMapping }: { crmObjectType: string; updatedFieldMapping: FieldMapping }
  ) {
    if (crmObjectType in state.formFieldMappings) {
      state.formFieldMappings[crmObjectType].mappings = state.formFieldMappings[crmObjectType].mappings.map(
        fieldMapping => {
          if (fieldMapping.id === updatedFieldMapping.id) {
            return {
              ...fieldMapping,
              ...updatedFieldMapping
            };
          }
          return fieldMapping;
        }
      );
    }
  },
  discardFormFieldMappingChanges(state: State, crmObjectType: string) {
    if (!state?.formFieldMappings[crmObjectType]?.mappings || !state?.prevFormFieldMappings[crmObjectType]) {
      return;
    }
    state.formFieldMappings[crmObjectType].mappings = state.prevFormFieldMappings[crmObjectType];
    const defaultErrorArray = new Array(state.formFieldMappings[crmObjectType].mappings.length).fill(null);
    state.formFieldMappingRowError[crmObjectType] = defaultErrorArray;
  },
  setSaveFormFieldMappingLoading(
    state: State,
    { crmObjectType, isSaveFormFieldMappingLoading }: { crmObjectType: string; isSaveFormFieldMappingLoading: boolean }
  ) {
    state.isSaveFormFieldMappingLoading[crmObjectType] = isSaveFormFieldMappingLoading;
  },
  setCreateDefaultFormFieldMappingsLoading(state: State, isCreateDefaultFormFieldMappingsLoading: boolean) {
    state.isCreateDefaultFormFieldMappingsLoading = isCreateDefaultFormFieldMappingsLoading;
  }
};

export default mutations;
